import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, FileText, Trash2, CheckCircle2, RefreshCw } from "lucide-react";
import { ORDERED_VENDORS } from "@/lib/vendors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalize = (str) =>
    new Set(
        (str || "")
            .toLowerCase()
            .replace(/\.pdf$/i, "")
            .replace(/[^a-z0-9]+/g, " ")
            .split(" ")
            .filter(Boolean)
    );

const jaccard = (a, b) => {
    const inter = [...a].filter((x) => b.has(x)).length;
    const union = new Set([...a, ...b]).size;
    return union === 0 ? 0 : inter / union;
};

// Greedily match each file to its best-scoring, still-unclaimed course.
const autoMatch = (files, courses) => {
    const scored = [];
    files.forEach((file, fi) => {
        const fTokens = normalize(file.name);
        courses.forEach((course) => {
            const cTokens = normalize(course.title);
            scored.push({ fi, courseId: course.id, score: jaccard(fTokens, cTokens) });
        });
    });
    scored.sort((a, b) => b.score - a.score);

    const usedFiles = new Set();
    const usedCourses = new Set();
    const assignment = {}; // fi -> courseId
    scored.forEach(({ fi, courseId, score }) => {
        if (score < 0.34) return;
        if (usedFiles.has(fi) || usedCourses.has(courseId)) return;
        usedFiles.add(fi);
        usedCourses.add(courseId);
        assignment[fi] = courseId;
    });
    return assignment;
};

const BrochureManager = ({ token }) => {
    const [vendorSlug, setVendorSlug] = useState(ORDERED_VENDORS[0]?.slug || "");
    const [existing, setExisting] = useState([]); // [{course_id, filename, uploaded_at}]
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState([]); // [{file, courseId}]
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const vendor = useMemo(() => ORDERED_VENDORS.find((v) => v.slug === vendorSlug), [vendorSlug]);
    const uploadedByCourse = useMemo(() => {
        const map = {};
        existing.forEach((b) => { map[String(b.course_id)] = b; });
        return map;
    }, [existing]);

    const fetchExisting = async (slug) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/admin/brochures`, {
                params: { vendor_slug: slug },
                headers: { "X-Admin-Token": token },
            });
            setExisting(res.data);
        } catch (err) {
            toast.error("Unable to load existing brochures.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (vendorSlug) fetchExisting(vendorSlug);
        setPending([]);
    }, [vendorSlug]);

    const handleFiles = (fileList) => {
        const files = Array.from(fileList).filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
        if (!files.length) {
            toast.error("Only PDF files are accepted.");
            return;
        }
        const assignment = autoMatch(files, vendor?.courses || []);
        const next = files.map((file, fi) => ({
            file,
            courseId: assignment[fi] != null ? String(assignment[fi]) : "",
        }));
        setPending(next);
        const matchedCount = next.filter((p) => p.courseId).length;
        toast.success(`${matchedCount}/${files.length} file(s) auto-matched to courses. Review below.`);
    };

    const updatePendingCourse = (idx, courseId) => {
        setPending((prev) => prev.map((p, i) => (i === idx ? { ...p, courseId } : p)));
    };

    const removePending = (idx) => setPending((prev) => prev.filter((_, i) => i !== idx));

    const uploadOne = async (file, courseId) => {
        const course = vendor.courses.find((c) => String(c.id) === String(courseId));
        const form = new FormData();
        form.append("vendor_slug", vendor.slug);
        form.append("course_id", String(courseId));
        form.append("course_title", course?.title || "");
        form.append("file", file);
        await axios.post(`${API}/admin/brochures/upload`, form, {
            headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
        });
    };

    const uploadAllPending = async () => {
        const ready = pending.filter((p) => p.courseId);
        if (!ready.length) {
            toast.error("Assign a course to at least one file before uploading.");
            return;
        }
        setUploading(true);
        let ok = 0;
        for (const p of ready) {
            try {
                await uploadOne(p.file, p.courseId);
                ok += 1;
            } catch (err) {
                toast.error(`Failed to upload ${p.file.name}`);
            }
        }
        toast.success(`Uploaded ${ok}/${ready.length} brochure(s).`);
        setUploading(false);
        setPending([]);
        fetchExisting(vendorSlug);
    };

    const uploadSingleForCourse = async (courseId, file) => {
        setUploading(true);
        try {
            await uploadOne(file, courseId);
            toast.success("Brochure uploaded.");
            fetchExisting(vendorSlug);
        } catch (err) {
            toast.error("Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const deleteBrochure = async (courseId) => {
        try {
            await axios.delete(`${API}/admin/brochures/${vendorSlug}/${courseId}`, {
                headers: { "X-Admin-Token": token },
            });
            toast.success("Brochure removed.");
            fetchExisting(vendorSlug);
        } catch (err) {
            toast.error("Unable to remove brochure.");
        }
    };

    if (!vendor) return null;

    return (
        <div className="mt-6" data-testid="brochure-manager">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <label className="text-xs uppercase tracking-widest text-[#75668C] font-display">Partner</label>
                    <select
                        data-testid="brochure-vendor-select"
                        value={vendorSlug}
                        onChange={(e) => setVendorSlug(e.target.value)}
                        className="sxe-input mt-1"
                    >
                        {ORDERED_VENDORS.map((v) => (
                            <option key={v.slug} value={v.slug}>{v.name}</option>
                        ))}
                    </select>
                </div>
                <button onClick={() => fetchExisting(vendorSlug)} className="btn-secondary">
                    <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* Bulk drop zone */}
            <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handleFiles(e.dataTransfer.files);
                }}
                className={`mt-6 rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
                    dragOver ? "border-[#A100FF] bg-[#A100FF]/5" : "border-white/15"
                }`}
                data-testid="brochure-dropzone"
            >
                <UploadCloud className="w-8 h-8 mx-auto text-[#C266FF]" />
                <p className="text-white font-display mt-3">Drop multiple brochure PDFs here</p>
                <p className="text-[#75668C] text-sm mt-1">
                    Files are auto-matched to {vendor.name} courses by filename. You can fix any mismatches before uploading.
                </p>
                <label className="btn-secondary inline-flex mt-4 cursor-pointer">
                    Browse files
                    <input
                        type="file"
                        accept="application/pdf"
                        multiple
                        className="hidden"
                        data-testid="brochure-file-input"
                        onChange={(e) => e.target.files.length && handleFiles(e.target.files)}
                    />
                </label>
            </div>

            {/* Pending matches review */}
            {pending.length > 0 && (
                <div className="mt-6 sxe-card p-5" data-testid="brochure-pending-list">
                    <div className="flex items-center justify-between">
                        <div className="text-white font-display font-medium">{pending.length} file(s) staged</div>
                        <button onClick={uploadAllPending} disabled={uploading} className="btn-primary disabled:opacity-50">
                            {uploading ? "Uploading..." : "Upload all matched"}
                        </button>
                    </div>
                    <div className="grid gap-3 mt-4">
                        {pending.map((p, idx) => (
                            <div key={idx} className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
                                <FileText className="w-4 h-4 text-[#C266FF] shrink-0" />
                                <span className="text-sm text-[#E9CCFF] truncate flex-1">{p.file.name}</span>
                                <select
                                    value={p.courseId}
                                    onChange={(e) => updatePendingCourse(idx, e.target.value)}
                                    className="sxe-input !w-64 !py-1.5 text-sm"
                                    data-testid={`brochure-pending-select-${idx}`}
                                >
                                    <option value="">— assign course —</option>
                                    {vendor.courses.map((c) => (
                                        <option key={c.id} value={c.id}>{c.title}</option>
                                    ))}
                                </select>
                                <button onClick={() => removePending(idx)} className="text-[#75668C] hover:text-red-400">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Per-course status list */}
            <div className="grid gap-3 mt-8">
                {vendor.courses.map((c) => {
                    const uploaded = uploadedByCourse[String(c.id)];
                    return (
                        <div key={c.id} className="sxe-card p-4 flex items-center gap-4" data-testid={`brochure-course-row-${c.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-white font-display text-sm truncate">{c.title}</div>
                                {uploaded ? (
                                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 mt-1">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Uploaded {new Date(uploaded.uploaded_at).toLocaleString()}
                                    </div>
                                ) : (
                                    <div className="text-[11px] text-[#75668C] mt-1">No brochure uploaded</div>
                                )}
                            </div>
                            <label className="btn-secondary shrink-0 cursor-pointer text-xs">
                                {uploaded ? "Replace" : "Upload"}
                                <input
                                    type="file"
                                    accept="application/pdf"
                                    className="hidden"
                                    onChange={(e) => e.target.files[0] && uploadSingleForCourse(c.id, e.target.files[0])}
                                />
                            </label>
                            {uploaded && (
                                <button onClick={() => deleteBrochure(c.id)} className="text-[#75668C] hover:text-red-400 shrink-0">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BrochureManager;
